import { supabase } from '@/lib/supabase';
import { triggerPushForUser } from '@/lib/notifyPush';
import { notificationTitle, notificationTargetUrl, notificationTargetPath } from '@/lib/pushDevices';
import { buildSubdomain } from '@/lib/domain';

// ─────────────────────────────────────────────────────────────
// Notifications de la cloche d'en-tête (table `notifications`).
// Centralise l'émission des événements métier côté marchand :
// nouveau message, nouvelle commande, nouvelle réservation,
// nouvelle demande de contact... Tout est BEST-EFFORT : l'échec
// d'une notification ne doit jamais faire échouer l'action métier.
// ─────────────────────────────────────────────────────────────

export type NotificationType =
  | 'order'
  | 'message'
  | 'contact'
  | 'booking'
  | 'payment'
  | 'vendor'
  | 'stock'
  | 'form'
  | 'system';

export interface PushNotificationInput {
  /** Destinataire de la notification (le marchand). */
  userId: number | null | undefined;
  /** Émetteur (client, visiteur...). 0 si inconnu. */
  senderId?: number | null;
  /** Publication / entité liée (produit, annonce...). 0 si aucune. */
  publicationId?: number | null;
  type: NotificationType | string;
  message: string;
}

function toInt(value: number | null | undefined): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
}

/**
 * Résout l'ORIGINE RÉELLE du destinataire (son propre sous-domaine ou son
 * domaine personnalisé vérifié) — jamais celle de la personne qui déclenche
 * la notification. Sans cela, un client qui écrit depuis son propre espace
 * envoyait un lien de notification construit sur SON origine à lui, ce qui
 * renvoyait le marchand vers une page inexistante / le mauvais domaine au
 * clic. Best-effort : chaîne vide si indéterminable (repli sur l'origine
 * courante dans notificationTargetUrl).
 */
async function resolveRecipientOrigin(userId: number): Promise<string> {
  try {
    const { data: domainRow } = await supabase
      .from('websitedomain')
      .select('domaine')
      .eq('user_id', userId)
      .or('is_active.eq.true,verified.eq.true')
      .limit(1)
      .maybeSingle();
    const domaine = (domainRow?.domaine as string | undefined) || '';
    if (domaine.trim()) return `https://${domaine.trim()}`;
  } catch {
    /* best-effort */
  }
  try {
    const { data: userRow } = await supabase.from('users').select('user_name').eq('id', userId).maybeSingle();
    const userName = (userRow?.user_name as string | undefined) || '';
    if (userName.trim()) return buildSubdomain(userName);
  } catch {
    /* best-effort */
  }
  return '';
}

/**
 * Insère une notification pour la cloche d'en-tête. Best-effort :
 * silencieux en cas d'erreur (l'action métier reste valide).
 */
export async function pushNotification(input: PushNotificationInput): Promise<void> {
  const userId = toInt(input.userId);
  const message = (input.message || '').trim();
  if (!userId || !message) return;
  try {
    await supabase.from('notifications').insert({
      user_id: userId,
      sender_id: toInt(input.senderId),
      publication_id: toInt(input.publicationId),
      type: input.type,
      message: message.slice(0, 240),
      is_read: 0,
    });
    // 📲 Notification PWA (Web Push) : atteint le téléphone MÊME app fermée.
    // On transmet explicitement le titre, le contenu ET la cible : cliquer sur
    // la notification ouvre directement la page concernée (commande, message,
    // réservation…) au lieu de retomber sur le tableau de bord.
    // ⚠️ L'URL doit pointer vers le sous-domaine / domaine du DESTINATAIRE,
    // jamais vers l'origine de la personne qui déclenche la notification
    // (ex : un client qui écrit depuis son espace ne doit pas construire le
    // lien avec SON origine à lui).
    const recipientOrigin = await resolveRecipientOrigin(userId);
    const path = notificationTargetPath(input.type, { publicationId: input.publicationId });
    void triggerPushForUser(userId, {
      title: notificationTitle(input.type),
      body: message.slice(0, 180),
      tag: `zifek-${Date.now()}`,
      type: String(input.type),
      url: recipientOrigin ? `${recipientOrigin}${path}` : notificationTargetUrl(input.type, { publicationId: input.publicationId }),
    });
  } catch {
    /* best-effort : on ignore les erreurs de notification */
  }
}

export interface LowStockItem {
  name: string;
  stock: number;
}

/**
 * Alerte de stock faible/rupture : une seule notification listant les noms
 * précis des produits concernés (affichée dans la cloche ET en notification
 * système PWA, avec le son d'alerte). Best-effort.
 */
export async function notifyLowStockAlert(
  userId: number | null | undefined,
  items: LowStockItem[],
): Promise<void> {
  const valid = (items || []).filter((i) => i && i.name);
  if (valid.length === 0) return;
  const shown = valid.slice(0, 6).map((i) => `${i.name} (${Math.max(0, Math.trunc(i.stock)) || 0})`);
  const more = valid.length > 6 ? ` et ${valid.length - 6} autre(s) produit(s)` : '';
  const label = valid.length === 1 ? 'Alerte stock' : 'Alerte stock faible';
  await pushNotification({
    userId,
    type: 'stock',
    message: `${label} : ${shown.join(', ')}${more}. Pensez à réapprovisionner.`,
  });
}